import React from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  IconButton, 
  alpha,
  CardHeader,
  Collapse,
  Tooltip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import BlockIcon from '@mui/icons-material/Block';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Lead } from '../Inflow';

// Simplified StyledCard component
const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
  borderLeft: '4px solid',
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  '&:hover': {
    boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
    transform: 'translateY(-2px)',
    transition: 'all 0.2s ease'
  },
  userSelect: 'none',
  position: 'relative',
  zIndex: 1,
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
})) as typeof Card;

// Style for the expand icon that rotates when expanded/collapsed
const ExpandButton = styled(IconButton)(() => ({
  marginLeft: 'auto',
}));

interface LeadCardProps {
  lead: Lead;
  onEdit: (lead: Lead) => void;
  onDelete: (id: string) => void;
  onMove: (id: string, status: 'hot' | 'cold' | 'obsolete' | 'win') => void;
  isDragging?: boolean;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

const getStatusColor = (status: string): string => {
  switch (status) {
    case 'hot':
      return '#f44336'; // red
    case 'cold':
      return '#2196f3'; // blue
    case 'obsolete':
      return '#9e9e9e'; // grey
    case 'win':
      return '#4caf50'; // green
    default:
      return '#9c27b0'; // purple
  }
};

const LeadCard: React.FC<LeadCardProps> = ({ lead, onEdit, onDelete, onMove, isDragging = false, isCollapsed, onToggleCollapse }) => {
  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onEdit(lead);
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onDelete(lead.id);
  };

  const handleMove = (status: 'hot' | 'cold' | 'obsolete' | 'win', e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onMove(lead.id, status);
  };

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onToggleCollapse();
  };

  const getStatusIcon = () => {
    switch (lead.status) {
      case 'hot':
        return <LocalFireDepartmentIcon fontSize="small" sx={{ color: "orange" }} />;
      case 'cold':
        return <AcUnitIcon fontSize="small" sx={{ color: "#03a9f4" }} />;
      case 'obsolete':
        return <BlockIcon fontSize="small" color="action" />;
      case 'win':
        return <EmojiEventsIcon fontSize="small" sx={{ color: "#ffc107" }} />;
      default:
        return null;
    }
  };

  return (
    <StyledCard 
      sx={{ 
        borderLeftColor: getStatusColor(lead.status),
        position: 'relative',
        // Simplified styles for dragging state
        ...(isDragging && {
          backgroundColor: alpha(getStatusColor(lead.status), 0.12),
          boxShadow: `0 8px 16px ${alpha(getStatusColor(lead.status), 0.3)}`,
          opacity: 0.9,
        }),
        userSelect: 'none',
        '&:hover': {
          boxShadow: '0 6px 12px rgba(0,0,0,0.15)',
          transform: 'translateY(-3px)',
        }
      }}
      component="div"
    >
      {/* Visual indicator for dragging state */}
      {isDragging && (
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          border: `2px dashed ${getStatusColor(lead.status)}`,
          borderRadius: 'inherit',
          pointerEvents: 'none',
        }} />
      )}
      
      <CardHeader
        sx={{ py: 1, px: 2 }}
        title={
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
              {getStatusIcon()}
            </Box>
            <Typography variant="subtitle2" noWrap>
              {lead.name}
            </Typography>
          </Box>
        }
        action={
          <Box 
            sx={{ display: 'flex', alignItems: 'center' }}
            onClick={e => e.stopPropagation()}
          >
            {/* Drag indicator for visual feedback only - actual drag comes from the parent */}
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mr: 1,
                opacity: 0.6,
                '&:hover': { opacity: 1 },
                cursor: 'grab',
              }}
            >
              <DragIndicatorIcon fontSize="small" sx={{ color: 'primary.main' }} />
            </Box>
            
            <IconButton
              color="primary"
              size="small"
              onClick={handleEdit}
              sx={{ 
                mr: 1,
                p: 1,
                bgcolor: 'primary.main',
                color: 'white',
                '&:hover': {
                  bgcolor: 'primary.dark',
                  transform: 'scale(1.05)'
                },
                transition: 'all 0.2s ease'
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
            
            <ExpandButton 
              onClick={handleToggle}
              size="small"
              aria-expanded={!isCollapsed}
              aria-label="show more"
            >
              {isCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
            </ExpandButton>
          </Box>
        }
      />
      
      <Collapse in={!isCollapsed} timeout="auto" unmountOnExit>
        <CardContent sx={{ py: 1, px: 2 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Phone:</strong> {lead.phone}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Email:</strong> {lead.email}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Source:</strong> {lead.source}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Last Contact:</strong> {lead.lastContact}
          </Typography>
          
          {lead.notes && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              <strong>Notes:</strong> {lead.notes}
            </Typography>
          )}
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}
               onClick={e => e.stopPropagation()}>
            {lead.status !== 'hot' && (
              <IconButton 
                size="small" 
                title="Move to Hot" 
                onClick={(e) => handleMove('hot', e)}
              >
                <LocalFireDepartmentIcon fontSize="small" sx={{ color: "orange" }} />
              </IconButton>
            )}
            {lead.status !== 'cold' && (
              <IconButton 
                size="small" 
                title="Move to Cold" 
                onClick={(e) => handleMove('cold', e)}
              >
                <AcUnitIcon fontSize="small" sx={{ color: "#03a9f4" }} />
              </IconButton>
            )}
            {lead.status !== 'obsolete' && (
              <IconButton 
                size="small" 
                title="Move to Obsolete" 
                onClick={(e) => handleMove('obsolete', e)}
              >
                <BlockIcon fontSize="small" color="action" />
              </IconButton>
            )}
            {lead.status !== 'win' && (
              <IconButton 
                size="small" 
                title="Move to Win" 
                onClick={(e) => handleMove('win', e)}
              >
                <EmojiEventsIcon fontSize="small" sx={{ color: "#ffc107" }} />
              </IconButton>
            )}
            <Tooltip title="Delete lead">
              <IconButton 
                size="small" 
                onClick={handleDeleteClick}
                sx={{ 
                  color: 'error.main',
                  '&:hover': {
                    bgcolor: alpha('#f44336', 0.1),
                    transform: 'scale(1.05)'
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </CardContent>
      </Collapse>
    </StyledCard>
  );
};

export default LeadCard; 